import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaOptionsType } from "embla-carousel";
import { Box, Flex, IconButton } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

interface CarouselProps {
  children: React.ReactNode[];
  options?: EmblaOptionsType;
  showArrows?: boolean;
  showDots?: boolean;
  autoplayMs?: number;
}

const Carousel = ({
  children,
  options,
  showArrows = true,
  showDots = false,
  autoplayMs,
}: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (i: number) => emblaApi?.scrollTo(i),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !autoplayMs) return;
    const id = setInterval(() => {
      emblaApi.scrollNext();
    }, autoplayMs);
    return () => clearInterval(id);
  }, [emblaApi, autoplayMs]);

  return (
    <Box position="relative">
      <Box ref={emblaRef} overflow="hidden">
        <Flex>{children}</Flex>
      </Box>

      {showArrows && (
        <>
          <IconButton
            aria-label="Previous"
            icon={<ChevronLeftIcon />}
            position="absolute"
            left={2}
            top="50%"
            transform="translateY(-50%)"
            onClick={scrollPrev}
            bg="blackAlpha.700"
            color="white"
            _hover={{ bg: "blackAlpha.900" }}
            borderRadius="full"
            size="sm"
            zIndex={2}
          />
          <IconButton
            aria-label="Next"
            icon={<ChevronRightIcon />}
            position="absolute"
            right={2}
            top="50%"
            transform="translateY(-50%)"
            onClick={scrollNext}
            bg="blackAlpha.700"
            color="white"
            _hover={{ bg: "blackAlpha.900" }}
            borderRadius="full"
            size="sm"
            zIndex={2}
          />
        </>
      )}

      {showDots && (
        <Flex
          justify="center"
          gap={2}
          position="absolute"
          bottom={4}
          left="50%"
          transform="translateX(-50%)"
          zIndex={2}
        >
          {children.map((_, i) => (
            <Box
              key={i}
              w="6px"
              h="6px"
              borderRadius="full"
              bg={i === selectedIndex ? "white" : "whiteAlpha.500"}
              cursor="pointer"
              transition="background 0.2s"
              onClick={() => scrollTo(i)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default Carousel;
